import { useCdfRawValues } from 'features/dataLayers'
import { isNil } from 'lodash'
import { useMemo } from 'react'
import {
  contains,
  GeckoComponentDataFrame,
  GeckoComponentRect,
  GeckoMeasurementRange,
} from 'types'

/**
 * An optimized helper hook which fetches CDF file data and returns the percent of values in the cdf within the filter range
 * @param cdf             The component data frame to pull data from
 * @param bounds          Optional bounds to limit search size to
 */
export const useGetPercentInFilterRange = (
  cdf: GeckoComponentDataFrame | undefined,
  bounds?: GeckoComponentRect | undefined,
  filterValueRange?: GeckoMeasurementRange
): {
  percentInRange: number | undefined
  numberOfBins: number | undefined
  numberOfBinsWithData: number | undefined
  isLoading: boolean
} => {
  //----------------------------------------------------------------------------

  const { buffer, isLoading } = useCdfRawValues(cdf)

  const data = useMemo(() => {
    if (!cdf || !buffer || !bounds)
      return {
        percentInRange: undefined,
        numberOfBins: undefined,
        numberOfBinsWithData: undefined,
      }

    const cdfXStart = cdf.data.geometry.start.x.num
    const cdfYStart = cdf.data.geometry.start.y.num
    const cdfBinWidth = cdf.data.binning.bin_size.width.toNumber(
      cdf.data.geometry.x.unit
    )
    const cdfBinHeight = cdf.data.binning.bin_size.height.toNumber(
      cdf.data.geometry.y.unit
    )

    const projectedBounds = bounds.project(cdf.data.geometry.space)

    const boundsBinXStart =
      (projectedBounds.start.x.num - cdfXStart) / cdfBinWidth
    const boundsBinXEnd = (projectedBounds.end.x.num - cdfXStart) / cdfBinWidth
    const boundsBinYStart =
      (projectedBounds.start.y.num - cdfYStart) / cdfBinHeight
    const boundsBinYEnd = (projectedBounds.end.y.num - cdfYStart) / cdfBinHeight

    let totalCount = 0
    let countInRange = 0
    let numberOfBins = 0
    let numberOfBinsWithData = 0
    //format the value range
    const cdfUnit = cdf.data.params.measurement_unit
    const filterHigh = filterValueRange?.end.convertTo(cdfUnit).num
    const filterLow = filterValueRange?.start.convertTo(cdfUnit).num

    for (let i = 0; i < buffer.values.length; i++) {
      const binX = i % cdf.data.binning.x_bin_count
      const binY = Math.floor(i / cdf.data.binning.x_bin_count)
      const midBinX = binX + 0.5
      const midBinY = binY + 0.5

      if (
        !!bounds &&
        !contains(
          boundsBinXStart,
          boundsBinYStart,
          boundsBinXEnd,
          boundsBinYEnd,
          midBinX,
          midBinY
        )
      )
        continue
      numberOfBins++

      const num = buffer.values.at(i)
      if (Number.isNaN(num) || isNil(num)) continue

      if (
        num <= (filterHigh ?? Number.POSITIVE_INFINITY) &&
        num >= (filterLow ?? Number.NEGATIVE_INFINITY)
      ) {
        countInRange++
        numberOfBinsWithData++
      }

      totalCount++
    }

    return {
      percentInRange: totalCount === 0 ? 0 : (countInRange / totalCount) * 100,
      numberOfBins,
      numberOfBinsWithData,
    }
  }, [buffer, bounds, cdf, filterValueRange])

  return { ...data, isLoading }
}

/**
 * An optimized helper hook which fetches CDF file data and returns sum of all values in the cdf within the bounds
 * @param cdf             The component data frame to pull data from
 * @param bounds          Optional bounds to limit search size to
 */
export const useGetSumOfSection = (
  cdf: GeckoComponentDataFrame | undefined,
  bounds?: GeckoComponentRect | undefined
): {
  sum: number | undefined
  isLoading: boolean
} => {
  //----------------------------------------------------------------------------

  const { buffer, isLoading } = useCdfRawValues(cdf)

  const data = useMemo(() => {
    if (!cdf || !buffer || !bounds)
      return {
        sum: undefined,
      }

    const cdfXStart = cdf.data.geometry.start.x.num
    const cdfYStart = cdf.data.geometry.start.y.num
    const cdfBinWidth = cdf.data.binning.bin_size.width.toNumber(
      cdf.data.geometry.x.unit
    )
    const cdfBinHeight = cdf.data.binning.bin_size.height.toNumber(
      cdf.data.geometry.y.unit
    )

    const projectedBounds = bounds.project(cdf.data.geometry.space)

    const boundsBinXStart =
      (projectedBounds.start.x.num - cdfXStart) / cdfBinWidth
    const boundsBinXEnd = (projectedBounds.end.x.num - cdfXStart) / cdfBinWidth
    const boundsBinYStart =
      (projectedBounds.start.y.num - cdfYStart) / cdfBinHeight
    const boundsBinYEnd = (projectedBounds.end.y.num - cdfYStart) / cdfBinHeight

    let sum = 0

    for (let i = 0; i < buffer.values.length; i++) {
      const num = buffer.values.at(i)
      if (Number.isNaN(num) || isNil(num)) continue

      const binX = i % cdf.data.binning.x_bin_count
      const binY = Math.floor(i / cdf.data.binning.x_bin_count)
      const midBinX = binX + 0.5
      const midBinY = binY + 0.5

      if (
        !!bounds &&
        !contains(
          boundsBinXStart,
          boundsBinYStart,
          boundsBinXEnd,
          boundsBinYEnd,
          midBinX,
          midBinY
        )
      )
        continue

      sum += num
    }

    return {
      sum,
    }
  }, [buffer, bounds, cdf])

  return { ...data, isLoading }
}
